// src/components/customer-search/CustomerColumnConfig.ts

import { SvgIconComponent } from "@mui/icons-material";
import { MyCustomerApiResponse } from "../../../store/services/customerService";

// Enum för de olika typer av kunddata som kan visas
export enum CustomerDataField {
  // Standardfält
  NAME = "name",
  CUSTOMER_ID = "customerId",
  SEGMENT = "segment",
  JOURNEY_TYPE = "journeyType",
  CONSUMPTION = "consumption",
  LAST_CONTACT = "lastContact",
  LAST_LOGIN = "lastLogin",
  NEXT_ACTION = "nextAction",

  // Ytterligare fält som kan konfigureras
  SALES_REP = "salesRep",
  POSTAL_CITY = "postalCity",
  CONTRACT_TYPE = "contractType",
  EMAIL = "email",
  PHONE = "phone",
  CUSTOMER_SINCE = "customerSince",
  VIP_STATUS = "vipStatus",
}

// Interface för kolumnkonfiguration
export interface ColumnConfig {
  field: CustomerDataField;
  headerLabel: string;
  visible: boolean;
  sortable?: boolean;
  width?: string;
  renderCell?: (customer: MyCustomerApiResponse) => React.ReactNode;
  icon?: SvgIconComponent;
  priority?: number; // För kortvy - lägre värde visas först
}

// Interface för kundkortkonfiguration
export interface CardFieldConfig {
  field: CustomerDataField;
  label?: string;
  visible: boolean;
  icon?: SvgIconComponent;
  renderField?: (customer: MyCustomerApiResponse) => React.ReactNode;
  priority?: number; // Lägre värde visas först
  section?: "header" | "metadata" | "activity"; // Var i kortet fältet ska visas
}

// Standardkolumner för kundsökningen
export const DEFAULT_CUSTOMER_COLUMNS: ColumnConfig[] = [
  {
    field: CustomerDataField.NAME,
    headerLabel: "Kund",
    visible: true,
    sortable: true,
    priority: 1,
  },
  {
    field: CustomerDataField.SEGMENT,
    headerLabel: "Segment",
    visible: true,
    sortable: true,
    priority: 2,
  },
  {
    field: CustomerDataField.JOURNEY_TYPE,
    headerLabel: "Kundtyp",
    visible: false,
    sortable: true,
    priority: 3,
  },
  {
    field: CustomerDataField.CONSUMPTION,
    headerLabel: "Förbrukning",
    visible: true,
    sortable: true,
    priority: 4,
  },
  {
    field: CustomerDataField.LAST_LOGIN,
    headerLabel: "Senast inloggad",
    visible: true,
    sortable: true,
    priority: 6,
  },
  {
    field: CustomerDataField.LAST_CONTACT,
    headerLabel: "Senast kontakt",
    visible: true,
    priority: 6,
  },
  {
    field: CustomerDataField.NEXT_ACTION,
    headerLabel: "Nästa aktivitet",
    visible: true,
    sortable: true,
    priority: 7,
  },

  {
    field: CustomerDataField.SALES_REP,
    headerLabel: "Säljare",
    visible: false, // Dold som standard
    sortable: true,
    priority: 8,
  },
  {
    field: CustomerDataField.POSTAL_CITY,
    headerLabel: "Ort",
    visible: false, // Dold som standard
    sortable: true,
    priority: 9,
  },
];

// Standardfält för kundkort
export const DEFAULT_CARD_FIELDS: CardFieldConfig[] = [
  {
    field: CustomerDataField.NAME,
    visible: true,
    priority: 1,
    section: "header",
  },
  {
    field: CustomerDataField.CUSTOMER_ID,
    visible: true,
    priority: 2,
    section: "header",
  },
  {
    field: CustomerDataField.SEGMENT,
    visible: true,
    priority: 3,
    section: "header",
  },
  {
    field: CustomerDataField.JOURNEY_TYPE,
    label: "Kundtyp",
    visible: true,
    priority: 4,
    section: "metadata",
  },
  {
    field: CustomerDataField.CONSUMPTION,
    label: "Förbrukning",
    visible: true,
    priority: 5,
    section: "metadata",
  },
  {
    field: CustomerDataField.EMAIL,
    label: "E-post",
    visible: true,
    priority: 6,
    section: "metadata",
  },
  {
    field: CustomerDataField.PHONE,
    label: "Telefon",
    visible: true,
    priority: 7,
    section: "metadata",
  },
  {
    field: CustomerDataField.LAST_CONTACT,
    label: "Senast kontakt",
    visible: true,
    priority: 8,
    section: "activity",
  },
  {
    field: CustomerDataField.LAST_LOGIN,
    label: "Senast inloggad",
    visible: true,
    priority: 9,
    section: "activity",
  },
  {
    field: CustomerDataField.NEXT_ACTION,
    label: "Nästa aktivitet",
    visible: true,
    priority: 10,
    section: "activity",
  },
  {
    field: CustomerDataField.SALES_REP,
    label: "Säljare",
    visible: false, // Dold som standard
    priority: 11,
    section: "metadata",
  },
];

// Konfiguration för Prospect/Suspect-vyn
export const PROSPECT_COLUMNS: ColumnConfig[] = [
  ...DEFAULT_CUSTOMER_COLUMNS,
  {
    field: CustomerDataField.VIP_STATUS,
    headerLabel: "VIP Status",
    visible: true,
    sortable: true,
    priority: 3, // Visa tidigare i listan
  },
];

// Konfiguration för Prospect/Suspect kortvy
export const PROSPECT_CARD_FIELDS: CardFieldConfig[] = [
  ...DEFAULT_CARD_FIELDS,
  {
    field: CustomerDataField.VIP_STATUS,
    label: "VIP Status",
    visible: true,
    priority: 4, // Visa tidigt i metadata
    section: "metadata",
  },
];

// Funktion för att slå ihop standardkonfigurationen med kundspecifik konfiguration
export function mergeColumnConfigs(
  defaults: ColumnConfig[],
  custom?: Partial<ColumnConfig>[]
): ColumnConfig[] {
  if (!custom || custom.length === 0) return defaults;

  // Kopiera standardkonfigurationen
  const result = [...defaults];

  // Uppdatera med anpassade inställningar
  custom.forEach((customConfig) => {
    if (!customConfig.field) return;

    const existingIndex = result.findIndex(
      (c) => c.field === customConfig.field
    );
    if (existingIndex >= 0) {
      // Uppdatera befintlig konfiguration
      result[existingIndex] = { ...result[existingIndex], ...customConfig };
    } else {
      // Lägg till ny kolumn
      result.push(customConfig as ColumnConfig);
    }
  });

  return result;
}

// Funktion för att slå ihop kortfältkonfigurationer
export function mergeCardFieldConfigs(
  defaults: CardFieldConfig[],
  custom?: Partial<CardFieldConfig>[]
): CardFieldConfig[] {
  if (!custom || custom.length === 0) return defaults;

  // Kopiera standardkonfigurationen
  const result = [...defaults];

  // Uppdatera med anpassade inställningar
  custom.forEach((customConfig) => {
    if (!customConfig.field) return;

    const existingIndex = result.findIndex(
      (c) => c.field === customConfig.field
    );
    if (existingIndex >= 0) {
      // Uppdatera befintlig konfiguration
      result[existingIndex] = { ...result[existingIndex], ...customConfig };
    } else {
      // Lägg till nytt fält
      result.push(customConfig as CardFieldConfig);
    }
  });

  return result;
}
